import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAssistance, useVehicle } from "../hooks";
import { FaCheck } from "react-icons/fa";
import {
  BatteryIcon,
  FlatTireIcon,
  OverheatIcon,
  WornBrakeIcon,
} from "../components/common/icons";
import { Text } from "@chakra-ui/react";
import {
  BreakdownSeverity,
  BreakdownSeverityColor,
  BreakdownTire,
  BreakdownType,
} from "../enums";
import { Trans, useTranslation } from "react-i18next";
import CrashIcon from "../components/common/icons/CrashIcon";

export const BreakdownContext = React.createContext({});

/**
 * The breakdown context provider
 * 
 * @param {Object} props The props of the component
 * @returns The breakdown context provider
 */
const BreakdownProvider = ({ children }) => {
  const { t } = useTranslation();
  const { vehicle } = useVehicle();
  const { assistance } = useAssistance();

  const [selectedBreakdownIndex, setSelectedBreakdownIndex] = useState(0);

  const hasCollision = useMemo(() => assistance?.category === "ecall", [assistance?.category]);

  const isEngineOverheating = useMemo(
    () => vehicle?.Vehicle?.Powertrain?.CombustionEngine?.EOT > 115,
    [vehicle?.Vehicle?.Powertrain?.CombustionEngine?.EOT],
  );

  const isBatteryLow = useMemo(
    () =>
      vehicle?.Vehicle?.LowVoltageBattery?.CurrentVoltage <
      vehicle?.Vehicle?.LowVoltageBattery?.NominalVoltage * 0.8,
    [
      vehicle?.Vehicle?.LowVoltageBattery?.CurrentVoltage,
      vehicle?.Vehicle?.LowVoltageBattery?.NominalVoltage,
    ],
  );

  const isFrontLeftBrakeWorn = useMemo(
    () =>
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn,
    [vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn],
  );

  const isFrontRightBrakeWorn = useMemo(
    () =>
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn,
    [vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn],
  );

  const isRearLeftBrakeWorn = useMemo(
    () =>
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn,
    [vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn],
  );

  const isRearRightBrakeWorn = useMemo(
    () =>
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn,
    [vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn],
  );

  const isBrakeSystemFaulty = useMemo(
    () =>
      isFrontLeftBrakeWorn &&
      isFrontRightBrakeWorn &&
      isRearLeftBrakeWorn &&
      isRearRightBrakeWorn,
    [
      isFrontLeftBrakeWorn,
      isFrontRightBrakeWorn,
      isRearLeftBrakeWorn,
      isRearRightBrakeWorn
    ]
  )

  const isFrontLeftTirePressureLow = useMemo(
    () =>
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow,
    [vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow],
  );

  const isFrontRightTirePressureLow = useMemo(
    () =>
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow,
    [vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow],
  );

  const isRearLeftTirePressureLow = useMemo(
    () =>
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow,
    [vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow],
  );

  const isRearRightTirePressureLow = useMemo(
    () =>
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow,
    [vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow],
  );

  const frontLeftTireInformation = useMemo(
    () =>
      `${vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.TireWidth || "---"
      } / R ${vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.TireAspectRatio ||
      "---"
      } ${vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.TireDiameter ||
      "---"
      } Y`,
    [
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.TireAspectRatio,
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.TireDiameter,
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.TireWidth,
    ],
  );

  const frontRightTireInformation = useMemo(
    () =>
      `${vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.TireWidth || "---"
      } / R ${vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.TireAspectRatio ||
      "---"
      } ${vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.TireDiameter ||
      "---"
      } Y`,
    [
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.TireAspectRatio,
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.TireDiameter,
      vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.TireWidth,
    ],
  );

  const rearLeftTireInformation = useMemo(
    () =>
      `${vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.TireWidth || "---"
      } / R ${vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.TireAspectRatio ||
      "---"
      } ${vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.TireDiameter ||
      "---"
      } Y`,
    [
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.TireAspectRatio,
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.TireDiameter,
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.TireWidth,
    ],
  );

  const rearRightTireInformation = useMemo(
    () =>
      `${vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.TireWidth || "---"
      } / R ${vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.TireAspectRatio ||
      "---"
      } ${vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.TireDiameter ||
      "---"
      } Y`,
    [
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.TireAspectRatio,
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.TireDiameter,
      vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.TireWidth,
    ],
  );

  const breakdownList = useMemo(() => {
    const breakdowns = [
      {
        color: BreakdownSeverityColor[BreakdownSeverity.CRITICAL],
        icon: CrashIcon,
        status: "Collision",
        label: "Collision",
        active: hasCollision,
        type: BreakdownType.ENGINE,
      },
      {
        color: BreakdownSeverityColor[BreakdownSeverity.CRITICAL],
        icon: OverheatIcon,
        status: t("plugin.breakdown.engineOverheat"),
        label: t("plugin.breakdown.engineOverheatDetail"),
        active: isEngineOverheating,
        type: BreakdownType.ENGINE,
      },
      {
        color: BreakdownSeverityColor[BreakdownSeverity.CRITICAL],
        icon: BatteryIcon,
        status: t("plugin.breakdown.batteryLow"),
        label: t("plugin.breakdown.batteryLowDetail"),
        active: isBatteryLow,
        type: BreakdownType.ENGINE,
      },
      {
        color: BreakdownSeverityColor[BreakdownSeverity.CRITICAL],
        icon: FlatTireIcon,
        status: t("plugin.breakdown.flatTire"),
        label: {
          [BreakdownTire.FRONT_LEFT]: (
            <>
              <Text>
                <Trans values={{ tire: t("plugin.breakdown.tire.frontLeft") }}>
                  plugin.breakdown.flatTireDetail
                </Trans>
              </Text>
              <Text fontWeight="bold">[{frontLeftTireInformation}]</Text>
            </>
          ),
          [BreakdownTire.FRONT_RIGHT]: (
            <>
              <Text>
                <Trans values={{ tire: t("plugin.breakdown.tire.frontRight") }}>
                  plugin.breakdown.flatTireDetail
                </Trans>
              </Text>
              <Text fontWeight="bold">[{frontRightTireInformation}]</Text>
            </>
          ),
          [BreakdownTire.REAR_LEFT]: (
            <>
              <Text>
                <Trans values={{ tire: t("plugin.breakdown.tire.rearLeft") }}>
                  plugin.breakdown.flatTireDetail
                </Trans>
              </Text>
              <Text fontWeight="bold">[{rearLeftTireInformation}]</Text>
            </>
          ),
          [BreakdownTire.REAR_RIGHT]: (
            <>
              <Text>
                <Trans values={{ tire: t("plugin.breakdown.tire.rearRight") }}>
                  plugin.breakdown.flatTireDetail
                </Trans>
              </Text>
              <Text fontWeight="bold">[{rearRightTireInformation}]</Text>
            </>
          ),
        },
        active:
          isFrontLeftTirePressureLow ||
          isFrontRightTirePressureLow ||
          isRearLeftTirePressureLow ||
          isRearRightTirePressureLow,
        type: BreakdownType.TIRE,
        tire: {
          [BreakdownTire.FRONT_LEFT]: isFrontLeftTirePressureLow,
          [BreakdownTire.FRONT_RIGHT]: isFrontRightTirePressureLow,
          [BreakdownTire.REAR_LEFT]: isRearLeftTirePressureLow,
          [BreakdownTire.REAR_RIGHT]: isRearRightTirePressureLow,
        },
      },
      {
        color: BreakdownSeverityColor[BreakdownSeverity.WARNING],
        icon: WornBrakeIcon,
        status: t("plugin.breakdown.wornBrake"),
        label: {
          [BreakdownTire.FRONT_LEFT]: t("plugin.breakdown.wornBrakeDetail", {
            brake: t("plugin.breakdown.brake.frontLeft"),
          }),
          [BreakdownTire.FRONT_RIGHT]: t("plugin.breakdown.wornBrakeDetail", {
            brake: t("plugin.breakdown.brake.frontRight"),
          }),
          [BreakdownTire.REAR_LEFT]: t("plugin.breakdown.wornBrakeDetail", {
            brake: t("plugin.breakdown.brake.rearLeft"),
          }),
          [BreakdownTire.REAR_RIGHT]: t("plugin.breakdown.wornBrakeDetail", {
            brake: t("plugin.breakdown.brake.rearRight"),
          }),
        },
        active:
          (isFrontLeftBrakeWorn ||
            isFrontRightBrakeWorn ||
            isRearLeftBrakeWorn ||
            isRearRightBrakeWorn) && !isBrakeSystemFaulty,
        type: BreakdownType.TIRE,
        tire: {
          [BreakdownTire.FRONT_LEFT]: isFrontLeftBrakeWorn,
          [BreakdownTire.FRONT_RIGHT]: isFrontRightBrakeWorn,
          [BreakdownTire.REAR_LEFT]: isRearLeftBrakeWorn,
          [BreakdownTire.REAR_RIGHT]: isRearRightBrakeWorn,
        },
      },
      {
        color: BreakdownSeverityColor[BreakdownSeverity.CRITICAL],
        icon: WornBrakeIcon,
        status: t("plugin.breakdown.noBrake"),
        label: {
          [BreakdownTire.FRONT_LEFT]: t("plugin.breakdown.noBrakeDetail", {
            brake: t("plugin.breakdown.brake.frontLeft"),
          }),
          [BreakdownTire.FRONT_RIGHT]: t("plugin.breakdown.noBrakeDetail", {
            brake: t("plugin.breakdown.brake.frontRight"),
          }),
          [BreakdownTire.REAR_LEFT]: t("plugin.breakdown.noBrakeDetail", {
            brake: t("plugin.breakdown.brake.rearLeft"),
          }),
          [BreakdownTire.REAR_RIGHT]: t("plugin.breakdown.noBrakeDetail", {
            brake: t("plugin.breakdown.brake.rearRight"),
          }),
        },
        active: isBrakeSystemFaulty,
        type: BreakdownType.TIRE,
        tire: {
          [BreakdownTire.FRONT_LEFT]: isFrontLeftBrakeWorn,
          [BreakdownTire.FRONT_RIGHT]: isFrontRightBrakeWorn,
          [BreakdownTire.REAR_LEFT]: isRearLeftBrakeWorn,
          [BreakdownTire.REAR_RIGHT]: isRearRightBrakeWorn,
        },
      },
    ].filter((warning) => !!warning.active);

    if (breakdowns.length === 0) {
      breakdowns.push({
        color: BreakdownSeverityColor[BreakdownSeverity.NONE],
        icon: FaCheck,
        status: t("plugin.breakdown.none"),
        label: "Everything looks fine",
        type: BreakdownType.ENGINE,
      });
    }
    return breakdowns;
  }, [
    hasCollision,
    frontLeftTireInformation,
    frontRightTireInformation,
    isEngineOverheating,
    isBatteryLow,
    isFrontLeftBrakeWorn,
    isFrontLeftTirePressureLow,
    isFrontRightBrakeWorn,
    isFrontRightTirePressureLow,
    isRearLeftBrakeWorn,
    isRearLeftTirePressureLow,
    isRearRightBrakeWorn,
    isRearRightTirePressureLow,
    rearLeftTireInformation,
    rearRightTireInformation,
    isBrakeSystemFaulty,
    t,
  ]);

  const breakdownListLength = useMemo(
    () => breakdownList.length,
    [breakdownList],
  );

  const selectedBreakdown = useMemo(() => {
    return breakdownList[selectedBreakdownIndex];
  }, [breakdownList, selectedBreakdownIndex]);

  /**
   * Handles the breakdown selection
   * 
   * @param {Number} index The index of the breakdown
   */
  const selectBreakdown = useCallback((index) => {
    setSelectedBreakdownIndex(index);
  }, []);

  /**
   * Handles the next breakdown selection
   */
  const selectNextBreakdown = useCallback(() => {
    if (selectedBreakdownIndex === breakdownListLength - 1) {
      setSelectedBreakdownIndex(0);
    } else {
      setSelectedBreakdownIndex(selectedBreakdownIndex + 1);
    }
  }, [breakdownListLength, selectedBreakdownIndex]);

  /**
   * Handles the previous breakdown selection
   */
  const selectPreviousBreakdown = useCallback(() => {
    if (selectedBreakdownIndex === 0) {
      setSelectedBreakdownIndex(breakdownListLength - 1);
    } else {
      setSelectedBreakdownIndex(selectedBreakdownIndex - 1);
    }
  }, [breakdownListLength, selectedBreakdownIndex]);

  useEffect(() => {
    if (selectedBreakdownIndex > breakdownListLength - 1) {
      setSelectedBreakdownIndex(0);
    } else if (selectedBreakdownIndex < 0) {
      setSelectedBreakdownIndex(breakdownListLength - 1);
    }
  }, [breakdownListLength, selectedBreakdownIndex]);

  const contextValue = useMemo(
    () => ({
      breakdownList,
      breakdownListLength,
      selectBreakdown,
      selectedBreakdown,
      selectedBreakdownIndex,
      selectNextBreakdown,
      selectPreviousBreakdown,
    }),
    [
      breakdownList,
      breakdownListLength,
      selectBreakdown,
      selectedBreakdown,
      selectedBreakdownIndex,
      selectNextBreakdown,
      selectPreviousBreakdown,
    ],
  );

  return (
    <BreakdownContext.Provider value={contextValue}>
      {children}
    </BreakdownContext.Provider>
  );
};

export default React.memo(BreakdownProvider);
